import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DownloadIcon from '@mui/icons-material/Download';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HistoryIcon from '@mui/icons-material/History';
import { Box, Button, Stack, Typography } from '@mui/material';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import CircularProgress from '@mui/material/CircularProgress';
import { useQueryClient } from '@tanstack/react-query';
import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import {
	getFutureAndGeneratedInvoicesQueryKey,
	getUseInvoiceQueryKey,
	useInvoice,
	usePayInvoiceMutation,
	useReverseInvoiceMutation,
	useTransactionsForInvoice,
} from '../../api/InvoiceApi';
import { getUsePolicyQueryKey } from '../../api/PolicyApi';
import {
	getInvoicesForPolicyholderQueryKey,
	useInvoicesForPolicyholder,
} from '../../api/PolicyholderApi';
import {
	AuthContext,
	AuthData,
	UI_CASH_MANAGEMENT_FEATURE_ENABLED,
} from '../../context/auth';
import { ConfigContext, ConfigData } from '../../context/config';
import { PolicyContext, PolicyData } from '../../context/policy';
import { Payment } from '../../types/payments';
import {
	PaymentCreateRequest,
	PaymentReversalReason,
} from '../../types/policy';
import { getTransactionSummary } from '../../util/util';
import { StyledEmptyMessage } from '../common';
import DropdownButton, { HiddenActionItem } from '../DropdownButton';
import InvoiceSummary from '../InvoiceSummary';
import PayInvoiceModal from '../PayInvoiceModal';
import PaymentAccordions from '../PaymentAccordions';
import NewPayInvoiceModal from '../payments/components/PayInvoiceModal';
import NewReversePaymentModal from '../payments/components/ReversePaymentModal';
import ReversePaymentModal from '../ReversePaymentModal';
import { infoToast } from '../Toast';
import TransactionsDetailTable from '../TransactionsDetailTable';
import {
	POLICYHOLDER_INVOICE_VIEW__COPY_INVOICE_BUTTON,
	POLICYHOLDER_INVOICE_VIEW__COPY_INVOICE_SUCCESS_TOAST,
	POLICYHOLDER_INVOICE_VIEW__DOWNLOAD_INVOICE_BUTTON,
	POLICYHOLDER_INVOICE_VIEW__PAY_INVOICE_BUTTON,
	POLICYHOLDER_INVOICE_VIEW__REVERSE_PAYMENT_BUTTON_TEXT,
	POLICYHOLDER_INVOICE_VIEW__TITLE,
} from './data/constants';

interface Props {
	authData: AuthData;
	configData: ConfigData;
	policyData: PolicyData;
	onPayment?: (locator: string, req: PaymentCreateRequest) => void;
}

interface InvoiceModalProps {
	open: boolean;
	invoice?: any;
}

// Note: With exception with policyholder, currency related fields from invoice are used
// instead of using policy's currency
const InvoiceView = (props: Props) => {
	const params = useParams();
	const queryClient = useQueryClient();
	const { policyholderId, invoiceId } = params;
	const { authData } = props;
	const authToken = authData?.auth?.authorizationToken;
	const { hasPermission } = authData;
	const cashManagementEnabled =
		authData?.auth?.configProperties?.[UI_CASH_MANAGEMENT_FEATURE_ENABLED];
	const [expanded, setExpanded] = useState([] as string[]);
	const [payInvoiceModalInfo, setPayInvoiceModalInfo] =
		React.useState<InvoiceModalProps>({ invoice: null, open: false });
	const [reversePaymentModalOpen, setReversePaymentModalOpen] =
		React.useState<InvoiceModalProps>({ open: false, invoice: null });
	const payInvoiceMutation = usePayInvoiceMutation(authToken, queryClient);
	const reverseInvoiceMutation = useReverseInvoiceMutation(
		authToken,
		queryClient,
	);

	const invoicesForPolicyholderQueryKey = getInvoicesForPolicyholderQueryKey(
		authToken,
		policyholderId,
	);

	const {
		data: invoicesResponse,
		isLoading: isLoadingInvoicesForPolicyholder,
	} = useInvoicesForPolicyholder(
		authToken,
		policyholderId,
		hasPermission('invoice.fetch'),
	);
	const invoices = invoicesResponse?.data || [];
	const invoiceFromPolicyholderInvoices = invoices.find(
		(i) => i.displayId === invoiceId,
	);
	const invoiceLocator = invoiceFromPolicyholderInvoices?.locator;
	const { data: invoiceResponse, isLoading: isLoadingInvoice } = useInvoice(
		authToken,
		invoiceLocator,
	);
	const invoice = invoiceResponse?.data;

	const invoiceQueryKey = getUseInvoiceQueryKey(
		authToken,
		payInvoiceModalInfo?.invoice?.locator ||
			reversePaymentModalOpen.invoice?.locator ||
			invoice?.locator ||
			'',
	);

	const {
		data: transactionsResponse,
		isLoading: isLoadingTransactionsForInvoice,
	} = useTransactionsForInvoice(authToken, invoiceLocator);

	const transactions = transactionsResponse?.data.financialTransactions || [];
	const loading =
		isLoadingInvoicesForPolicyholder ||
		isLoadingInvoice ||
		isLoadingTransactionsForInvoice;

	const handleChange = (panel: any) => (event: any, isExpanded: boolean) => {
		const expandedAccordions = [...expanded];
		if (isExpanded) {
			expandedAccordions.push(panel);
		} else {
			const i = expanded.indexOf(panel);
			expandedAccordions.splice(i, 1);
		}
		setExpanded(expandedAccordions);
	};

	const refetchData = (policyId?: string) => {
		if (policyId) {
			const policyQueryKey = getUsePolicyQueryKey(policyId, authToken);
			queryClient.invalidateQueries(policyQueryKey);
		}
		queryClient.invalidateQueries(invoicesForPolicyholderQueryKey);
		queryClient.invalidateQueries(
			getFutureAndGeneratedInvoicesQueryKey(policyId, authToken),
		);
		queryClient.invalidateQueries(invoiceQueryKey);
	};

	const payInvoice = async (
		policyId: string,
		locator: string,
		paymentCreateReq: any,
	): Promise<Payment> => {
		const response = await payInvoiceMutation.mutateAsync({
			paymentCreateReq,
			invoiceLocator: locator,
		});
		refetchData(policyId);
		return response.data;
	};

	const reversePayment = async (
		paymentLocator: string,
		policyLocator?: string,
		reversalReason?: PaymentReversalReason,
	) => {
		await reverseInvoiceMutation.mutateAsync({
			paymentLocator,
			reversalReason,
		});
		refetchData();
	};

	if (loading) {
		return (
			<div
				style={{
					display: 'flex',
					width: '100%',
					justifyContent: 'center',
					padding: '1em',
				}}
			>
				<CircularProgress />
			</div>
		);
	}

	const renderTransactions = () => {
		const transactionSummary = invoice ? (
			<div
				style={{
					display: 'flex',
					justifyContent: 'space-between',
					width: '80%',
				}}
			>
				{getTransactionSummary(
					transactions,
					invoice,
					invoice?.totalDueCurrency,
				).map((total) => {
					const label = (
						<Box component='span' fontWeight='500'>
							{total.label}
						</Box>
					);
					return (
						<Typography sx={{ mr: 1 }} key={total.value.toString()}>
							{label}
							{total.value}
						</Typography>
					);
				})}
			</div>
		) : null;

		return transactions.length > 0 && invoice ? (
			<Accordion
				key={invoice.locator}
				expanded={expanded.includes(invoice.locator)}
				onChange={handleChange(invoice.locator)}
			>
				<AccordionSummary expandIcon={<ExpandMoreIcon />} id={invoice.locator}>
					{transactionSummary}
				</AccordionSummary>
				<AccordionDetails style={{ display: 'flex', flexDirection: 'column' }}>
					<TransactionsDetailTable
						transactions={transactions}
						currency={invoice?.totalDueCurrency}
						policyLocator={invoice.policyLocator}
					/>
				</AccordionDetails>
			</Accordion>
		) : null;
	};

	const noPaymentsMessage = () => {
		return <StyledEmptyMessage>No Payments</StyledEmptyMessage>;
	};

	const payInvoiceButton = () => {
		const invoiceStatus = invoice.settlementStatus;
		if (invoiceStatus !== 'settled' && hasPermission('invoice.pay')) {
			return (
				<PayInvoiceModal
					invoiceLocator={invoice.locator}
					isPaid={invoice.settlementStatus === 'settled'}
					onPayment={payInvoice}
					currency={invoice?.totalDueCurrency}
				/>
			);
		} else if (
			invoiceStatus === 'settled' &&
			invoice.settlementType === 'paid' &&
			hasPermission('payment.reverse')
		) {
			const latestPayment = invoice.payments.find(
				(p: Payment) => !p.reversedTimestamp,
			);
			if (latestPayment) {
				return (
					<ReversePaymentModal
						paymentLocator={latestPayment.locator}
						displayId={latestPayment.displayId}
						onReverse={reversePayment}
					/>
				);
			} else {
				return null;
			}
		} else {
			return null;
		}
	};

	const actions: HiddenActionItem[] = [];

	if (
		invoice.settlementStatus === 'settled' &&
		invoice.settlementType === 'paid' &&
		hasPermission('payment.reverse') &&
		cashManagementEnabled
	) {
		const latestPayment = invoice.payments.find(
			(p: Payment) => !p.reversedTimestamp,
		);
		if (latestPayment) {
			actions.push({
				label: POLICYHOLDER_INVOICE_VIEW__REVERSE_PAYMENT_BUTTON_TEXT,
				icon: <HistoryIcon />,
				callback: () => {
					setReversePaymentModalOpen({ open: true, invoice });
				},
			});
		}
	}

	if (invoice.documents[0]?.url) {
		actions.push({
			label: POLICYHOLDER_INVOICE_VIEW__DOWNLOAD_INVOICE_BUTTON,
			icon: <DownloadIcon />,
			callback: () => {
				window.open(invoice.documents[0].url, '_blank');
			},
		});
	}

	actions.push({
		label: POLICYHOLDER_INVOICE_VIEW__COPY_INVOICE_BUTTON,
		icon: <ContentCopyIcon />,
		callback: () => {
			navigator.clipboard.writeText(invoice.locator);
			infoToast(POLICYHOLDER_INVOICE_VIEW__COPY_INVOICE_SUCCESS_TOAST);
		},
	});

	return (
		<>
			<Stack spacing={2} direction='row' justifyContent='space-between' mb={2}>
				<Stack
					spacing={2}
					direction='row'
					justifyContent='space-between'
					alignItems='center'
				>
					<Typography variant='h3'>
						{POLICYHOLDER_INVOICE_VIEW__TITLE + ' ' + invoice.displayId}
					</Typography>
					{cashManagementEnabled &&
						invoice.settlementStatus === 'outstanding' &&
						hasPermission('invoice.pay') && (
							<Button
								variant='contained'
								onClick={() => setPayInvoiceModalInfo({ open: true, invoice })}
							>
								{POLICYHOLDER_INVOICE_VIEW__PAY_INVOICE_BUTTON}
							</Button>
						)}
					{!cashManagementEnabled && payInvoiceButton()}
				</Stack>
				<DropdownButton actions={actions} />
			</Stack>
			<InvoiceSummary invoice={invoice} authData={authData} />
			{renderTransactions()}
			{invoice && !!invoice.payments.length ? (
				<PaymentAccordions payments={invoice.payments as Payment[]} />
			) : (
				noPaymentsMessage()
			)}
			{payInvoiceModalInfo?.invoice && (
				<NewPayInvoiceModal
					callback={payInvoice}
					isCallbackLoading={payInvoiceMutation.isLoading}
					isCallbackSuccess={payInvoiceMutation.isSuccess}
					open={payInvoiceModalInfo.open}
					setOpen={() => {
						setPayInvoiceModalInfo({
							open: false,
						});
					}}
					invoice={payInvoiceModalInfo.invoice}
				/>
			)}
			{reversePaymentModalOpen?.invoice && (
				<NewReversePaymentModal
					callback={reversePayment}
					open={reversePaymentModalOpen.open}
					handleClose={() => {
						setReversePaymentModalOpen({
							open: false,
							invoice: null,
						});
					}}
					invoice={reversePaymentModalOpen.invoice}
				/>
			)}
		</>
	);
};

function withContext(Component: any) {
	return (props: Omit<Props, 'configData' | 'authData' | 'policyData'>) => (
		<ConfigContext.Consumer>
			{(configData) => (
				<AuthContext.Consumer>
					{(authData) => (
						<PolicyContext.Consumer>
							{(policyData) => (
								<Component
									{...props}
									configData={configData}
									authData={authData}
									policyData={policyData}
								/>
							)}
						</PolicyContext.Consumer>
					)}
				</AuthContext.Consumer>
			)}
		</ConfigContext.Consumer>
	);
}

export default withContext(InvoiceView);
